import React from 'react'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { CalendarIcon } from 'lucide-react'
import { format } from 'date-fns'
import { DateRange } from 'react-day-picker'
import { cn } from '@/lib/utils'

interface TimeRangeSelectorProps {
  value: string
  onChange: (value: string, customRange?: DateRange) => void
  showComparison?: boolean
  onComparisonToggle?: (enabled: boolean) => void
}

const presets = [
  { label: 'Today', value: 'today' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'Last 7 Days', value: '7d' },
  { label: 'Last 30 Days', value: '30d' },
  { label: 'Last 90 Days', value: '90d' },
  { label: 'This Month', value: 'month' },
  { label: 'Custom', value: 'custom' },
]

export default function TimeRangeSelector({
  value,
  onChange,
  showComparison = false,
  onComparisonToggle
}: TimeRangeSelectorProps) {
  const [dateRange, setDateRange] = React.useState<DateRange | undefined>()
  const [showCalendar, setShowCalendar] = React.useState(false)
  const [compareEnabled, setCompareEnabled] = React.useState(false)

  const handlePresetClick = (presetValue: string) => {
    if (presetValue === 'custom') {
      setShowCalendar(true)
    } else {
      onChange(presetValue)
      setShowCalendar(false)
    }
  }

  const handleDateRangeSelect = (range: DateRange | undefined) => {
    setDateRange(range)
    if (range?.from && range?.to) {
      onChange('custom', range)
      setShowCalendar(false)
    }
  }

  const handleComparisonToggle = () => {
    const newValue = !compareEnabled
    setCompareEnabled(newValue)
    onComparisonToggle?.(newValue)
  }

  const getDisplayLabel = () => {
    const preset = presets.find(p => p.value === value)
    if (preset && value !== 'custom') {
      return preset.label
    }
    if (dateRange?.from && dateRange?.to) {
      return `${format(dateRange.from, 'MMM dd')} - ${format(dateRange.to, 'MMM dd')}`
    }
    return 'Select range'
  }

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
      {/* Preset Buttons - scrollable on mobile */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 sm:pb-0 -mx-1 px-1 scrollbar-thin">
        {presets.slice(0, -1).map((preset) => (
          <Button
            key={preset.value}
            variant={value === preset.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => handlePresetClick(preset.value)}
            className={cn(
              'flex-shrink-0',
              value === preset.value && 'bg-gradient-brand hover:bg-gradient-brand-dark'
            )}
          >
            {preset.label}
          </Button>
        ))}

        {/* Custom Date Range Picker */}
        <Popover open={showCalendar} onOpenChange={setShowCalendar}>
          <PopoverTrigger asChild>
            <Button
              variant={value === 'custom' ? 'default' : 'outline'}
              size="sm"
              className={cn(
                'gap-2',
                value === 'custom' && 'bg-gradient-brand hover:bg-gradient-brand-dark'
              )}
            >
              <CalendarIcon className="h-4 w-4" />
              {value === 'custom' ? getDisplayLabel() : 'Custom'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar
              mode="range"
              selected={dateRange}
              onSelect={handleDateRangeSelect}
              numberOfMonths={2}
              defaultMonth={new Date()}
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* Comparison Toggle */}
      {showComparison && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleComparisonToggle}
          className={cn(
            compareEnabled && 'bg-brand-secondary-50 border-brand-secondary text-brand-secondary'
          )}
        >
          {compareEnabled ? '✓ ' : ''}Compare to previous
        </Button>
      )}
    </div>
  )
}
